import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Shield, Users as UsersIcon } from "lucide-react";
import { api, formatApiError } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";

const ROLE_OPTIONS = [
  { value: "admin",   label: "Super Admin",  hint: "Full access — can manage staff, products, orders, sales, POS." },
  { value: "manager", label: "Manager",      hint: "Manage products, orders, drivers, and POS. Cannot manage staff." },
  { value: "cashier", label: "Cashier",      hint: "POS only (front-counter sales)." },
  { value: "driver",  label: "Driver",       hint: "Only sees and updates their assigned deliveries." },
];

const ROLE_BADGE = {
  admin: "bg-[#C62828] text-white",
  manager: "bg-[#2F5233] text-white",
  cashier: "bg-[#E67E22] text-white",
  driver: "bg-[#1565C0] text-white",
};

const EMPTY = { email: "", password: "", name: "", role: "cashier", phone: "", branch: "Calamba" };

export default function AdminStaff() {
  const { user } = useAuth();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      setLoading(true);
      const r = await api.get("/users/staff");
      setStaff(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (s) => { setEditing(s); setForm({ email: s.email, password: "", name: s.name, role: s.role, phone: s.phone || "", branch: s.branch || "Calamba" }); setOpen(true); };

  const save = async () => {
    try {
      if (editing) {
        const payload = { name: form.name, role: form.role, phone: form.phone, branch: form.branch };
        if (form.password) payload.password = form.password;
        await api.put(`/users/staff/${editing.id}`, payload);
        toast.success("Staff updated");
      } else {
        await api.post("/users/staff", form);
        toast.success("Staff member created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const remove = async (s) => {
    if (!window.confirm(`Remove ${s.name} (${s.email})?`)) return;
    try {
      await api.delete(`/users/staff/${s.id}`);
      toast.success("Removed");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <h2 className="font-heading font-bold text-xl sm:text-2xl text-[#1C2826]">Staff & Access</h2>
          <p className="text-sm text-[#4A5D5A]">Manage who can log in to this console and what they can do.</p>
        </div>
        <Button onClick={openNew} className="bg-[#2F5233] hover:bg-[#1F3D24] text-white rounded-lg" data-testid="new-staff-btn">
          <Plus className="h-4 w-4 sm:mr-1" /> <span className="hidden sm:inline">Add staff</span>
        </Button>
      </div>

      {/* Role legend */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {ROLE_OPTIONS.map((r) => (
          <div key={r.value} className="bg-white border border-[#E1E5DF] rounded-xl p-3 text-xs">
            <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${ROLE_BADGE[r.value]}`}>
              <Shield className="h-3 w-3" /> {r.label}
            </span>
            <p className="mt-2 text-[#4A5D5A] leading-snug">{r.hint}</p>
          </div>
        ))}
      </div>

      <div className="bg-white border border-[#E1E5DF] rounded-lg overflow-x-auto">
        <table className="w-full text-sm min-w-[640px]">
          <thead className="bg-[#F9F9F7] text-left text-xs uppercase tracking-wider text-[#4A5D5A]">
            <tr>
              <th className="p-3">Name</th>
              <th className="p-3">Email</th>
              <th className="p-3">Role</th>
              <th className="p-3">Branch</th>
              <th className="p-3">Phone</th>
              <th className="p-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-[#E1E5DF]">
            {loading && <tr><td colSpan={6} className="p-8 text-center text-[#738380]">Loading…</td></tr>}
            {!loading && staff.length === 0 && <tr><td colSpan={6} className="p-10 text-center text-[#738380]"><UsersIcon className="h-6 w-6 mx-auto mb-2 opacity-50" />No staff yet</td></tr>}
            {staff.map((s) => (
              <tr key={s.id} data-testid={`staff-row-${s.id}`}>
                <td className="p-3 font-semibold text-[#1C2826]">
                  {s.name}
                  {s.id === user?.id && <span className="ml-2 text-[10px] font-bold text-[#2F5233]">(you)</span>}
                </td>
                <td className="p-3 text-[#4A5D5A]">{s.email}</td>
                <td className="p-3">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${ROLE_BADGE[s.role] || "bg-gray-400 text-white"}`}>
                    {s.role}
                  </span>
                </td>
                <td className="p-3 text-[#4A5D5A] text-xs">{s.branch || "—"}</td>
                <td className="p-3 text-[#4A5D5A]">{s.phone || "—"}</td>
                <td className="p-3 text-right whitespace-nowrap">
                  <Button size="sm" variant="ghost" onClick={() => openEdit(s)} data-testid={`edit-staff-${s.id}`}><Pencil className="h-4 w-4" /></Button>
                  {s.id !== user?.id && (
                    <Button size="sm" variant="ghost" onClick={() => remove(s)} data-testid={`delete-staff-${s.id}`}><Trash2 className="h-4 w-4 text-[#E74C3C]" /></Button>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader><DialogTitle>{editing ? "Edit staff" : "New staff member"}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><Label>Full name</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="staff-name" /></div>
            <div>
              <Label>Email</Label>
              <Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="staff-email" />
              {editing && <p className="text-[11px] text-[#738380] mt-1">Email cannot be changed.</p>}
            </div>
            <div>
              <Label>Role</Label>
              <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })}>
                <SelectTrigger data-testid="staff-role"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => <SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>)}
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#738380] mt-1">{ROLE_OPTIONS.find((r) => r.value === form.role)?.hint}</p>
            </div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="staff-phone" placeholder="+63 917 ..." /></div>
            <div>
              <Label>Branch</Label>
              <Select value={form.branch} onValueChange={(v) => setForm({ ...form, branch: v })}>
                <SelectTrigger data-testid="staff-branch"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="Calamba">Calamba (Main Office)</SelectItem>
                  <SelectItem value="Oroquieta City">Oroquieta City (Branch 1)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[11px] text-[#738380] mt-1">Used in shift reports and POS sales tagging.</p>
            </div>
            <div>
              <Label>{editing ? "New password (leave empty to keep)" : "Password"}</Label>
              <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="staff-password" placeholder={editing ? "••••••••" : "Min 6 characters"} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#2F5233] hover:bg-[#1F3D24] text-white" data-testid="staff-save">{editing ? "Save" : "Create"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
